import dayjs   from "dayjs";
import fromNow from "dayjs/plugin/relativeTime";
import utc     from "dayjs/plugin/utc";
import dynamic from "next/dynamic";

import { useGetDarkMode } from "_common/hooks/global";
import BackgroundWrapper  from "_components/atoms/backgroundWrapper";
import Icon               from "_components/atoms/icon";
import Image              from "_components/atoms/image";
import Typography         from "_components/atoms/typography";
import useStyles          from "_styles/organisms/questionCard";
import { palette }        from "_styles/theme";

const ReportFollowModal = dynamic ( () => import ( "_components/molecules/reportFollowModal" ), { ssr: false } );
const intToChar         = {
	0 : "A.",
	1 : "B.",
	2 : "C.",
	3 : "D."
};

const component = ( {
	isStudioPage, isPopOutChat, hasVotedValue,
	totalVotes, pollData, handleEndDeletePoll,
	handleVote, calcMaxPercentage,
	toggleDeleteModal, deleteModal
} ) => {

	const { options, title, isActive, id:pollId, createdAt, creator } = pollData;

	const { classes }          = useStyles ( );
	const maxVotesOption       = calcMaxPercentage ( options );
	const showPercentageFiller = hasVotedValue[pollId] || !isActive || isStudioPage || isPopOutChat;
	const darkMode             = useGetDarkMode ();

	dayjs.extend ( utc );
	dayjs.extend ( fromNow );
	const createdTS = dayjs ( dayjs.utc ( createdAt ) ).fromNow ();

	return (
		<>
			<div className = { classes.cardWrapper }>
				<div className = { classes.headerWrapper }>
					<div className = { classes.leftSide }>
						<Image
							alt       = "messageBanner"
							className = { classes.imageBanner }
							src       = { creator?.photo }
						/>

						<div className = { classes.infoTextWrapper }>
							<div className = { classes.nameContainer }>
								<Typography
									className = { classes.nameText }
									title     = { creator?.name }
								/>

								{creator?.isCeleb ? (
									<Icon sameInBothTheme
										className = { classes.verifiedIcon }
										name      = "verifiedProfile"
									/>
								) : null}
							</div>

							<div className = { classes.statsContainer }>

								<Typography
									className = { classes.liveText }
									title     = { isActive ? "Live" : "Ended" }
								/>

								<div className = { classes.dot } />

								<Typography
									className = { classes.liveText }
									title     = { createdTS }
								/>

								<div className = { classes.dot } />

								<Typography
									className = { classes.liveText }
									title     = { `${ totalVotes } Voted` }
								/>

							</div>

						</div>
					</div>

					{( isStudioPage || isPopOutChat ) && (
						<div>

							<ReportFollowModal
								from                = "polls"
								toggleDeleteModal   = { toggleDeleteModal }
							/>
						</div>
					)}
				</div>

				<Typography
					className = { classes.questionText }
					title     =  { title }
				/>

				<div style = { { display: "flex", gap: "2rem", flexDirection: "column" } }>
					{options?.map ( ( opt, idx ) => {
						const val          = totalVotes === 0 ? 0 : ( opt.votes / totalVotes ) * 100;
						const percentage   = Math.round ( val );
						const maxVotesFlag = maxVotesOption.votes === opt.votes;
						const showTick     = hasVotedValue[pollId] == opt.id;

						return (
							<BackgroundWrapper key = { opt?.id }
								className             = { classes.option }
								onClick               = { () => {
									if ( isActive && !hasVotedValue[pollId] ) {
										handleVote ( opt?.id );
									}
								} }
							>

								{( showPercentageFiller ) && (
									<div
										className = { classes.progressPercentage }
										style     = { {
											backgroundColor : ( maxVotesFlag && !isActive ) ? darkMode ? palette.darkModeDarkBlue50 : palette.primaryLightBlue : palette.darkModeDarkGrey20,
											width           : `${ percentage }%`
										} }
									/>
								)}

								<div style = { { display: "flex", justifyContent: "space-between", width: "100%", zIndex: "1", padding: "1rem 1.5rem" } }>
									<div style = { { display: "flex" } }>
										<Typography
											style = { { word: "40px", marginRight: "5px" } }
											title = { `${ intToChar[idx] }` }
										/>

										<Typography
											style = { { wordWrap: "anywhere", hyphens: "auto" } }
											title = { opt.title }
										/>

										{showTick && (
											<Icon sameInBothTheme
												className = { classes.tickIcon }
												name      = "tickIcon"
											/>
										)}
									</div>

									{( showPercentageFiller ) && (
										<Typography
											style = { { marginLeft: "1rem" } }
											title = { `${ percentage }%` }
										/>
									)}
								</div>

							</BackgroundWrapper>

						);
					} )}
				</div>

				{( isStudioPage || isPopOutChat ) && isActive && (
					<div className = { classes.endButtonSection }>
						<div className = { classes.endButton }
							onClick       = { () => handleEndDeletePoll ( false, false ) }
						>
							End
						</div>
					</div>
				)}
			</div>

			{deleteModal && (
				<>

					<Overlay
						showOverlay       = { deleteModal }
						toggleDeleteModal = { toggleDeleteModal }
					/>

					<DeleteModal
						handleEndDeletePoll  = { handleEndDeletePoll }
						isActive             = { isActive }
						toggleDeleteModal    = { toggleDeleteModal }
						totalVotes           = { totalVotes }
					/>
				</>
			)}
		</>
	);
};

export default component;

const Overlay = ( { showOverlay, toggleDeleteModal } ) => {
	const { classes } = useStyles ( undefined, { name: "styles" } );

	return (
		<div
			className =  { !showOverlay ? classes.overlay : classes.overlayShow }
			onClick   = { () => {
				if ( showOverlay ) toggleDeleteModal ();
			} }
		/>
	);
};

const DeleteModal = ( { totalVotes, toggleDeleteModal, isActive, handleEndDeletePoll } ) => {

	const { classes } = useStyles ( undefined, { name: "styles" } );

	return (
		<div className = { classes.deleteModalWrapper }>

			<div className = { classes.topSection }>

				<Typography
					className = { classes.headerText }
					title     = { "Delete Poll ?" }
				/>

				<Typography
					className = { classes.text1 }
					title     = { "Are you sure you want to delete this post?" }
				/>

				<Typography
					className = { classes.text2 }
					title     = { `${ totalVotes } viewers have already voted.` }
				/>

			</div>

			<div className = { classes.bottomSection }>

				<Typography
					className = { `${ classes.actionButtons }` }
					onClick   = { () => handleEndDeletePoll ( true, isActive ) }
					title     = { "Delete" }
				/>

				<Typography
					className = { `${ classes.actionButtons } ${ classes.cancelButtonColor }` }
					onClick   = { toggleDeleteModal }
					title     = { "Cancel" }
				/>

			</div>

		</div>
	);
};
